(() => {
  const SUGGEST_URL = '/search/suggest.json';
  const LIMIT = 8;

  const getOverlay = () => document.querySelector('[data-search-overlay]');
  const getContent = () => document.querySelector('[data-search-content]');
  const getInput = () => document.querySelector('[data-search-input]');
  const getResults = () => document.querySelector('[data-search-results]');
  const getDefault = () => document.querySelector('[data-search-default]');
  const getClear = () => document.querySelector('[data-search-clear]');
  const getCloseInline = () => document.querySelector('[data-search-form] [data-search-close]');
  const getTemplate = () => document.querySelector('[data-search-result-template]');

  let abortController = null;
  let debounceTimer = null;

  const setBodyLock = (locked) => {
    document.body.style.overflow = locked ? 'hidden' : '';
  };

  const showDefault = () => {
    getResults()?.classList.add('hidden');
    getDefault()?.classList.remove('hidden');
    getClear()?.classList.add('hidden');
    getCloseInline()?.classList.remove('hidden');
  };

  const showResults = () => {
    getResults()?.classList.remove('hidden');
    getDefault()?.classList.add('hidden');
    getClear()?.classList.remove('hidden');
    getCloseInline()?.classList.add('hidden');
  };

  const openOverlay = () => {
    const overlay = getOverlay();
    const content = getContent();
    if (!overlay || !content) return;
    overlay.classList.remove('opacity-0', 'pointer-events-none');
    overlay.classList.add('opacity-100', 'pointer-events-auto');
    overlay.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => {
      content.classList.remove('-translate-y-6', 'opacity-0');
      content.classList.add('translate-y-0', 'opacity-100');
    });
    setBodyLock(true);
    setTimeout(() => getInput()?.focus(), 150);
  };

  const closeOverlay = () => {
    const overlay = getOverlay();
    const content = getContent();
    const input = getInput();
    if (!overlay || !content) return;
    content.classList.add('-translate-y-6', 'opacity-0');
    content.classList.remove('translate-y-0', 'opacity-100');
    overlay.classList.add('opacity-0', 'pointer-events-none');
    overlay.classList.remove('opacity-100', 'pointer-events-auto');
    overlay.setAttribute('aria-hidden', 'true');
    setBodyLock(false);
    if (input) input.value = '';
    showDefault();
  };

  const formatMoney = (cents, currency) => {
    if (typeof cents !== 'number') return '';
    const value = cents / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: currency || 'GBP',
      }).format(value);
    } catch (e) {
      return `£${value.toFixed(2)}`;
    }
  };

  const renderProducts = (products, query) => {
    const results = getResults();
    const template = getTemplate();
    if (!results || !template) return;

    results.innerHTML = '';

    if (products.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'text-center py-12';
      empty.innerHTML = `
        <p class="text-foreground/50 text-base">No results found for &ldquo;${escapeHtml(query)}&rdquo;</p>
        <p class="text-foreground/30 text-sm mt-2">Try a different search term or browse our categories.</p>
      `;
      results.appendChild(empty);
      return;
    }

    const header = document.createElement('p');
    header.className = 'text-xs tracking-[0.3em] uppercase text-primary font-medium mb-4';
    header.textContent = `${products.length} Result${products.length !== 1 ? 's' : ''}`;
    results.appendChild(header);

    const list = document.createElement('div');
    list.className = 'flex flex-col gap-2';

    products.forEach((product) => {
      const clone = template.content.firstElementChild.cloneNode(true);
      clone.setAttribute('href', product.url);
      clone.setAttribute('data-search-close', '');

      const imageEl = clone.querySelector('[data-image]');
      if (imageEl && product.featured_image?.url) {
        imageEl.innerHTML = `<img src="${product.featured_image.url}&width=112" alt="" class="absolute inset-0 w-full h-full object-cover" loading="lazy" />`;
      }

      const vendor = clone.querySelector('[data-vendor]');
      if (vendor) vendor.textContent = product.vendor || '';

      const title = clone.querySelector('[data-title]');
      if (title) title.textContent = product.title;

      const desc = clone.querySelector('[data-description]');
      if (desc) desc.textContent = product.description ? product.description.replace(/<[^>]*>/g, '').slice(0, 120) : '';

      const price = clone.querySelector('[data-price]');
      if (price) {
        const priceValue = parseFloat(product.price);
        if (!isNaN(priceValue)) {
          price.textContent = formatMoney(Math.round(priceValue * 100), product.currency);
        }
      }

      list.appendChild(clone);
    });

    results.appendChild(list);
  };

  const escapeHtml = (str) =>
    String(str).replace(/[&<>"']/g, (s) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    })[s]);

  const fetchResults = async (query) => {
    if (abortController) abortController.abort();
    abortController = new AbortController();

    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product',
      'resources[limit]': LIMIT,
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,vendor,product_type,body',
    });

    try {
      const res = await fetch(`${SUGGEST_URL}?${params.toString()}`, {
        signal: abortController.signal,
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return;
      const data = await res.json();
      const products = (data.resources?.results?.products || []).filter(
        (p) => !(p.tags || []).some((t) => String(t).toLowerCase() === 'sheath')
      );
      renderProducts(products, query);
      showResults();
    } catch (e) {
      if (e.name !== 'AbortError') console.error('Search failed:', e);
    }
  };

  const handleInput = (e) => {
    const query = e.target.value.trim();
    clearTimeout(debounceTimer);

    if (query.length === 0) {
      showDefault();
      return;
    }

    debounceTimer = setTimeout(() => fetchResults(query), 200);
  };

  // Delegated click handlers
  document.addEventListener('click', (e) => {
    const openBtn = e.target.closest('[data-search-open]');
    if (openBtn) {
      e.preventDefault();
      openOverlay();
      return;
    }

    const closeBtn = e.target.closest('[data-search-close]');
    const backdrop = e.target.closest('[data-search-backdrop]');
    if (closeBtn || backdrop) {
      // If the close trigger is a real link, let navigation happen — just close the overlay visually.
      const isNavLink = closeBtn && closeBtn.tagName === 'A' && closeBtn.getAttribute('href') && closeBtn.getAttribute('href') !== '#';
      if (!isNavLink) {
        e.preventDefault();
      }
      closeOverlay();
      return;
    }

    const clearBtn = e.target.closest('[data-search-clear]');
    if (clearBtn) {
      e.preventDefault();
      const input = getInput();
      if (input) {
        input.value = '';
        input.focus();
      }
      showDefault();
    }
  });

  // Input listener (must attach after DOM ready)
  const initInput = () => {
    const input = getInput();
    if (input && !input.dataset.searchBound) {
      input.addEventListener('input', handleInput);
      input.dataset.searchBound = 'true';
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initInput);
  } else {
    initInput();
  }

  // Escape key closes overlay
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const overlay = getOverlay();
    if (overlay && overlay.classList.contains('opacity-100')) {
      closeOverlay();
    }
  });
})();
